import { useTranslation } from "react-i18next";
import { ProfileSectionProps } from "./types";
import { FormSection } from "@/features/common/components/FormSection";
import { DataField } from "@/features/common/components/DataField";
import { useFileUploader } from "@/components/features/FileUploader";
import { EducationStatusFields } from "./Qualification/EducationStatusFields";
import { EducationLocationFields } from "./Qualification/EducationLocationFields";
import { RecognitionStatusFields } from "./Qualification/RecognitionStatusFields";
import {
  getNursingEducationStatusLabelKey,
  getRecognitionStatusLabelKey
} from "../utils/formatters";
import { format } from "date-fns";
import { de } from "date-fns/locale";

/**
 * QualificationSection - Refactored into smaller sub-components.
 */
export function QualificationSection({
  profile,
  setProfile,
  loading,
  readOnly
}: ProfileSectionProps) {
  const { t } = useTranslation("talent");

  return (
    <FormSection title={t("forms.sections.qualifications.title")}>
      <div
        className={`grid gap-6 ${profile?.nursing_education_status === "in_progress" ? "grid-cols-3" : "grid-cols-2"}`}
      >
        <EducationStatusFields
          profile={profile}
          setProfile={setProfile}
          readOnly={readOnly}
          loading={loading}
        />

        <EducationLocationFields
          profile={profile}
          setProfile={setProfile}
          readOnly={readOnly}
        />

        <RecognitionStatusFields
          profile={profile}
          setProfile={setProfile}
          readOnly={readOnly}
          loading={loading}
        />
      </div>
    </FormSection>
  );
}

/**
 * QualificationView - Uses shared DataField for consistent display.
 */
export function QualificationView({ profile }: { profile: any }) {
  const { t } = useTranslation("talent");
  const { handleViewFile } = useFileUploader();

  const formatDate = (date?: string) =>
    date ? format(new Date(date), "dd.MM.yyyy", { locale: de }) : null;

  return (
    <div className="space-y-1">
      <DataField
        label="Pflegeausbildung Status"
        value={
          profile.nursing_education_status
            ? t(
                getNursingEducationStatusLabelKey(
                  profile.nursing_education_status
                )
              )
            : null
        }
      />
      <DataField
        label="Voraussichtlicher Abschluss"
        value={formatDate(profile.estimated_completion_date)}
      />
      <DataField
        label="Ausbildungsort"
        value={profile.education_address || profile.education_location}
      />
      <DataField
        label="Berufsanerkennung Status"
        value={
          profile.recognition_status
            ? t(getRecognitionStatusLabelKey(profile.recognition_status))
            : null
        }
      />
      <DataField
        label="Theoriestunden Defizit"
        value={profile.theory_hours_deficit}
      />
      <DataField
        label="Praxisstunden Defizit"
        value={profile.practice_hours_deficit}
      />
      {profile.deficit_notice_document && (
        <DataField
          label="Defizitbescheid"
          value={
            <button
              className="text-blue-600 hover:underline text-sm"
              onClick={() =>
                handleViewFile("profiles", profile.deficit_notice_document)
              }
            >
              Dokument anzeigen
            </button>
          }
        />
      )}
    </div>
  );
}
